<template>
  <div class="draw-point-buttons-container">
    <ButtonOutline
      :buttonText="t('general__text__cancel')"
      class="draw-point-buttons-container__button-cancel"
      @onClicked="onCancel"
    />
    <ButtonOutline
      :buttonText="t('general__text__select')"
      class="draw-point-buttons-container__button-submit"
      @onClicked="onSubmit"
    />
  </div>
  <GeojsonLayer
    v-if="layerBoundarySource"
    :layer="layerBoundaryStyle"
    :layerId="layerBoundaryId"
    :source="layerBoundarySource"
    :sourceId="layerBoundarySourceId"
  />
</template>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { booleanPointInPolygon } from '@turf/turf'
import { point as turfPoint } from '@turf/turf'
import { polygon as turfPolygon } from '@turf/turf'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import { GeoJSONSourceSpecification, LineLayerSpecification } from 'mapbox-gl'
import useMapbox from '@/composables/useMapbox'

const store = useStore()
const { t } = useI18n()
const { map } = useMapbox()

interface EMITS {
  (e: 'submitPoint', point: object | null)
  (e: 'submitPointMobile', point: object | null, editMode?: boolean)
}
const emits = defineEmits<EMITS>()

const props = defineProps({
  withinLayerId: {
    type: String,
    default: '',
    required: false
  },
  pointSource: {
    type: Object,
    default: null,
    required: false
  }
})

const mobileScreen = computed<boolean>(() => store.state.mobileScreen)

const layerBoundarySource = computed<GeoJSONSourceSpecification | null>(() => {
  if (!withinLayerSourceFeatures.value.length) return null
  const source: GeoJSONSourceSpecification = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: withinLayerSourceFeatures.value
    }
  }
  return source
})

const layerBoundaryId = ref<string>('layer-draw-point-boundary')
const layerBoundarySourceId = ref<string>('layer-draw-point-boundary-source')
const layerBoundaryStyle = ref<LineLayerSpecification>({
  id: layerBoundaryId.value,
  source: layerBoundarySourceId.value,
  type: 'line',
  layout: {
    'line-cap': 'round',
    'line-join': 'round'
  },
  paint: {
    'line-color': '#ff9800',
    'line-dasharray': [0.2, 2],
    'line-width': {
      base: 10,
      stops: [
        [14, 2],
        [18, 6],
        [22, 50]
      ]
    }
  }
})
const createPoint = ref<object | null>(null)
const draw = ref(
  new MapboxDraw({
    displayControlsDefault: false,
    defaultMode: 'simple_select',
    styles: [
      // White circle outline for deselected point
      {
        id: 'gl-draw-point-point-stroke-inactive',
        type: 'circle',
        filter: [
          'all',
          ['==', 'active', 'false'],
          ['==', '$type', 'Point'],
          ['==', 'meta', 'feature'],
          ['!=', 'mode', 'static']
        ],
        paint: {
          'circle-radius': ['interpolate', ['linear', 1.75], ['zoom'], 16, 2.25, 22, 13],
          'circle-opacity': 1,
          'circle-color': '#fff'
        }
      },
      // Inner circle for deselected point
      {
        id: 'gl-draw-point-inactive',
        type: 'circle',
        filter: [
          'all',
          ['==', 'active', 'false'],
          ['==', '$type', 'Point'],
          ['==', 'meta', 'feature'],
          ['!=', 'mode', 'static']
        ],
        paint: {
          'circle-radius': ['interpolate', ['linear', 1.75], ['zoom'], 16, 1.75, 22, 11],
          'circle-color': '#cc7a00',
          'circle-opacity': 0.8
        }
      },
      // White circle outline for selected point
      {
        id: 'gl-draw-point-stroke-active',
        type: 'circle',
        filter: [
          'all',
          ['==', '$type', 'Point'],
          ['==', 'active', 'true'],
          ['!=', 'meta', 'midpoint']
        ],
        paint: {
          'circle-radius': ['interpolate', ['linear', 1.75], ['zoom'], 16, 5, 22, 16],
          'circle-color': '#fff'
        }
      },
      // Inner circle for selected point
      {
        id: 'gl-draw-point-active',
        type: 'circle',
        filter: [
          'all',
          ['==', '$type', 'Point'],
          ['!=', 'meta', 'midpoint'],
          ['==', 'active', 'true']
        ],
        paint: {
          'circle-radius': ['interpolate', ['linear', 1.75], ['zoom'], 16, 4, 22, 14],
          'circle-color': '#ff9800'
        }
      }
      /*
      // This part of the styling seems unnecessary (?)
      {
        id: 'gl-draw-point-static',
        type: 'circle',
        filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Point']],
        paint: {
          'circle-radius': 5,
          'circle-color': '#ff9800'
        }
      }
      */
    ]
  })
)
const modifiedGeometry = ref<GeoJSON.Point>()
const withinLayerSourceFeatures = ref<GeoJSON.Feature[]>([])

//Methods
async function addDrawFeature(point) {
  await new Promise((resolve) => setTimeout(resolve, 100)) // Solve without delay???
  draw.value.add(point)
  draw.value.changeMode('simple_select', { featureIds: point.id })
}
function checkIfPointIsWithinLayer(pointCoordinate: number[], layerId: string) {
  if (!layerId.length) return false
  let isPointWithinPolygon = false
  const newPoint = turfPoint(pointCoordinate)
  withinLayerSourceFeatures.value?.forEach((feature) => {
    // MultiPolygon is removed since the code doesn't support it
    if (feature.geometry.type === 'Polygon' /* || feature.geometry.type === 'MultiPolygon' */) {
      const polygonBoundary = turfPolygon(feature.geometry.coordinates)
      if (booleanPointInPolygon(newPoint, polygonBoundary)) isPointWithinPolygon = true
    }
  })
  return isPointWithinPolygon
}
function onCancel() {
  draw.value.deleteAll().getAll()
  emits('submitPoint', null)
  emits('submitPointMobile', null)
}
function onSubmit() {
  let modifiedPoint: any | null = null
  // If geometry is not changed on submit:
  if (!modifiedGeometry.value) {
    draw.value.deleteAll().getAll()
    store.commit('setInfoMessage', {
      type: 'warning',
      message: mobileScreen.value
        ? t('message__draw__point-position-not-selected') // Currently used for both create and edit poi in mobile mode
        : t('message__draw__point-position-not-changed') // Currently only used for edit poi in desktop mode
    })
    if (mobileScreen.value) emits('submitPointMobile', null)
    return emits('submitPoint', null)
  }
  // Make sure new position is within layer boundaries (if withinLayerId isincluded):
  if (props.withinLayerId.length && map.getLayer(props.withinLayerId)) {
    if (!checkIfPointIsWithinLayer(modifiedGeometry.value.coordinates, props.withinLayerId)) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: t('message__draw__point-position-not-within-boundary')
      })
    } else {
      // New (created) or modified (existing) point to submit
      // Todo: on CREATE new point, we probably need to add point to poisForSelectedLevel array or make a new get pois request on submit
      modifiedPoint = createPoint.value ? createPoint.value : props.pointSource
      modifiedPoint.geometry = modifiedGeometry.value
      draw.value.deleteAll().getAll()
      store.commit('setInfoMessage', {
        type: 'success',
        message: t('message__draw__point-position-submitted')
      })
      if (mobileScreen.value)
        emits('submitPointMobile', modifiedPoint, createPoint.value ? false : true)
      return emits('submitPoint', modifiedPoint)
    }
  }
  // If free placement is allowed (no "withinLayerId" boundaries are included):
  if (!props.withinLayerId.length) {
    // Todo: on CREATE new point, we probably need to add point to poisForSelectedLevel array or make a new get pois request on submit
    modifiedPoint = createPoint.value ? createPoint.value : props.pointSource
    modifiedPoint.geometry = modifiedGeometry.value
    draw.value.deleteAll().getAll()
    store.commit('setInfoMessage', {
      type: 'success',
      message: t('message__draw__point-position-submitted')
    })
    if (mobileScreen.value)
      emits('submitPointMobile', modifiedPoint, createPoint.value ? false : true)
    emits('submitPoint', modifiedPoint)
  }
}
function updateGeometry() {
  const data = draw.value.getAll()
  if (!props.pointSource && !createPoint.value) createPoint.value = data.features[0]
  modifiedGeometry.value = data.features[0].geometry
}

onMounted(() => {
  if (!props.pointSource) map.on('draw.create', updateGeometry)
  map.on('draw.update', updateGeometry)
  if (props.pointSource) addDrawFeature(props.pointSource)
  if (props.withinLayerId.length) {
    // @ts-expect-error - map not typed
    const sourceId = map.getLayer(props.withinLayerId).source as string
    // @ts-expect-error - map not typed
    withinLayerSourceFeatures.value = map.getSource(sourceId)?._data.features
  }
  map.addControl(draw.value)
  if (!props.pointSource) draw.value.changeMode('draw_point')
})

onBeforeUnmount(() => {
  // @ts-expect-error - map not typed
  if (!props.pointSource) store.map.off('draw.create', updateGeometry)
  // @ts-expect-error - map not typed
  store.map.off('draw.update', updateGeometry)
  // @ts-expect-error - map not typed
  store.map.removeControl(draw.value)
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';

.draw-point-buttons-container {
  background-color: rgba(0, 0, 0, 0.7); // Same as in Ivion...
  border-radius: variables.$border-radius-large;
  border: none;
  display: flex;
  gap: variables.$gap-large;
  padding: variables.$padding-xlarge;
  position: absolute;
  top: variables.$margin-large;
  right: variables.$margin-large;
  z-index: variables.$z-index-overlay;
  &__button-cancel,
  &__button-submit {
    background: variables.$color-pure-white;
    width: 90px;
  }
}
</style>
